import { GetEmailCycleRecipientsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailCycleRecipientsRPC";
import { TypesSortOrder } from "@pulse/pulse-rpcs/lib/types/typesSortOrder";
import { LeoRPCResult, LeoUUID } from "@surya-digital/leo-ts-types";
import {
  applySnapshot,
  cast,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { getAPIClient } from "../../networking/APIClient";
import {
  createEmailCycleRecipientModel,
  EmailCycleRecipientModel,
} from "../models/EmailCycleRecipientModel";
import { useGetEmailCycleRecipientsRPCClientImpl } from "../rpcs/RPC";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  createRemoveEmailCycleRecipientStore,
  RemoveEmailCycleRecipientStore,
} from "./RemoveEmailCycleRecipientStore";
import {
  createDownloadEmailCycleRecipientsStore,
  DownloadEmailCycleRecipientsStore,
} from "./DownloadEmailCycleRecipientsStore";

export const EMAIL_CYCLE_RECIPIENTS_PER_PAGE = 10;

const getInitialState = (): SnapshotIn<typeof EmailCycleRecipientsStore> => ({
  recipients: [],
  totalItems: 0,
  loadingEmailCycleRecipients: false,
  getEmailCycleRecipientsRPCError: null,
  removeEmailCycleRecipientStore: createRemoveEmailCycleRecipientStore(),
  downloadEmailCycleRecipientsStore: createDownloadEmailCycleRecipientsStore(),
});

export const EmailCycleRecipientsStore = types
  .model("EmailCycleRecipientsStore", {
    recipients: types.array(EmailCycleRecipientModel),
    totalItems: types.integer,
    loadingEmailCycleRecipients: types.boolean,
    getEmailCycleRecipientsRPCError: types.maybeNull(
      types.enumeration(
        "GetEmailCycleRecipientsRPCError",
        Object.values(GetEmailCycleRecipientsRPC.RPCError),
      ),
    ),
    removeEmailCycleRecipientStore: RemoveEmailCycleRecipientStore,
    downloadEmailCycleRecipientsStore: DownloadEmailCycleRecipientsStore,
  })
  .actions((store) => ({
    getEmailCycleRecipients: flow(function* (
      cycleId: string,
      searchQuery = EMPTY_CHARACTER,
      pageIndex = 1,
      sortOrder = TypesSortOrder.TypesSortOrder.DESCENDING,
      sortColumn = GetEmailCycleRecipientsRPC.RequestEnums.SortColumn.SortColumn
        .ADDED_ON,
      loadingOptionsChange = false,
    ) {
      if (!loadingOptionsChange) store.loadingEmailCycleRecipients = true;
      store.getEmailCycleRecipientsRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const trimmedSearchQuery = searchQuery.trim();
        const request = new GetEmailCycleRecipientsRPC.Request(
          new LeoUUID(cycleId),
          pageIndex - 1,
          EMAIL_CYCLE_RECIPIENTS_PER_PAGE,
          sortOrder,
          sortColumn,
          trimmedSearchQuery.length ? trimmedSearchQuery : null,
        );

        const result: LeoRPCResult<
          GetEmailCycleRecipientsRPC.Response,
          GetEmailCycleRecipientsRPC.Errors.Errors
        > =
          yield useGetEmailCycleRecipientsRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) {
          store.recipients = cast(
            result.response.recipients.map((recipient) =>
              createEmailCycleRecipientModel({
                id: recipient.id.uuid,
                respondentId: recipient.respondentId,
                email: recipient.email,
                addedOn: recipient.addedOn.timestamp,
                status: recipient.status,
              }),
            ),
          );
          store.totalItems = result.response.totalItems;
          return;
        }

        switch (result.error.code) {
          case GetEmailCycleRecipientsRPC.RPCError.InvalidScheduleId:
          case GetEmailCycleRecipientsRPC.RPCError.InvalidPageIndex:
            store.getEmailCycleRecipientsRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        if (!loadingOptionsChange) store.loadingEmailCycleRecipients = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createEmailCycleRecipientsStore = (): Instance<
  typeof EmailCycleRecipientsStore
> => EmailCycleRecipientsStore.create(getInitialState());
